import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { api, apiDownload, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { EstadoEnvio, Paginated } from '@/lib/types';
import { ESTADO_ENVIO_COLOR, ESTADO_ENVIO_LABEL } from '@/lib/types';
import { EstadoEnvioSelector } from '@/components/domain/EstadoEnvioSelector';
import {
  useTablaPrefs,
  ResizeHandle,
  ColorMenu,
  colorPersonal,
  aplicarColor,
  type MenuColor,
} from '@/lib/uiPrefs';

/**
 * Reportes · Operaciones (14-sep-2026). Dos vistas (Marítimo/Aéreo y OTR) con
 * campos AUTO del Shipment Profile Report + campos MANUALES editables en
 * línea (click en la celda). Lo manual también se edita desde la tarjeta del
 * board ("Info adicional").
 */

export interface ApPago {
  id: number;
  entidad: string;
  transRef: string;
  creditor: string | null;
  creditorNombre: string | null;
  descripcion: string | null;
  moneda: string | null;
  monto: number | null;
  postDate: string | null;
  columna: string | null;
}

export interface OpsRow {
  bookingId: number;
  posicion: string;
  cliente: string;
  operativo: string;
  agenteId: number;
  cwStatus: string | null;
  estadoEnvio?: string;
  etd: string | null;
  eta: string | null;
  facturacionAuto?: string | null;
  apPagos?: ApPago[];
  [k: string]: unknown;
}

type Col = {
  key: string;
  label: string;
  /** Nombre completo para el tooltip cuando label es abreviatura */
  titulo?: string;
  manual?: boolean;
  ancho?: string;
  autoCapturable?: boolean;
  /** Acepta asignar una factura AP (pago a proveedor) además de texto libre */
  apAsignable?: boolean;
};

const COLS_MAR_AER: Col[] = [
  { key: 'estadoEnvio', label: 'Status' },
  { key: 'posicion', label: 'Posición' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'operativo', label: 'OP', titulo: 'Operativo' },
  { key: 'shipper', autoCapturable: true, label: 'Shipper' },
  { key: 'cnee', autoCapturable: true, label: 'Cnee' },
  { key: 'master', autoCapturable: true, label: 'MBL/MAWB' },
  { key: 'houseRef', autoCapturable: true, label: 'HBL/HAWB' },
  { key: 'origenPuerto', autoCapturable: true, label: 'POL/AOL' },
  { key: 'destinoPuerto', autoCapturable: true, label: 'POD/AOD' },
  { key: 'etd', label: 'ETD' },
  { key: 'eta', label: 'ETA' },
  { key: 'container', autoCapturable: true, label: 'Container' },
  { key: 'linea', autoCapturable: true, label: 'Line' },
  { key: 'vesselFlight', autoCapturable: true, label: 'Vessel/Flight' },
  { key: 'venta', label: 'Venta' },
  { key: 'costo', label: 'Costo' },
  { key: 'profit', label: 'Profit' },
  { key: 'facturacion', label: 'Facturación', manual: true },
  { key: 'bl', apAsignable: true, label: 'BL', manual: true },
  { key: 'locales', apAsignable: true, label: 'Locales', manual: true },
  { key: 'agente', apAsignable: true, label: 'Agente', manual: true },
  { key: 'inland', apAsignable: true, label: 'Inland', manual: true },
  { key: 'caat', apAsignable: true, label: 'CAAT', manual: true },
  { key: 'seguro', apAsignable: true, label: 'Seguro', manual: true },
  { key: 'otro', apAsignable: true, label: 'Otro', manual: true },
  { key: 'liberacion', label: 'Liberación', manual: true },
  { key: 'aa', label: 'AA', manual: true },
  { key: 'encomienda', label: 'Encomienda', manual: true },
  { key: 'garantia', label: 'Garantía', manual: true },
  { key: 'proveedor', label: 'Proveedor', manual: true },
  { key: 'demoras', label: 'Demoras', manual: true },
  { key: 'cerrado', label: 'Cerrado', manual: true },
  { key: 'comments', label: 'Comments', manual: true, ancho: 'min-w-[14rem]' },
];

const COLS_OTR: Col[] = [
  { key: 'estadoEnvio', label: 'Status' },
  { key: 'posicion', label: 'Posición' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'operativo', label: 'OP', titulo: 'Operativo' },
  { key: 'master', autoCapturable: true, label: 'BOL/PRO' },
  { key: 'cwStatus', label: 'Status CW' },
  { key: 'origenPuerto', autoCapturable: true, label: 'Origin' },
  { key: 'destinoPuerto', autoCapturable: true, label: 'Destination' },
  { key: 'etd', label: 'PU/ETD' },
  { key: 'eta', label: 'ETA' },
  { key: 'citaEntrega', label: 'Cita de entrega', manual: true },
  { key: 'estadias', label: 'Estadías', manual: true },
  { key: 'cajaUnidad', label: 'Caja/Unidad', manual: true },
  { key: 'linea', autoCapturable: true, label: 'Carrier' },
  { key: 'usa', apAsignable: true, label: 'USA', manual: true },
  { key: 'transfer', apAsignable: true, label: 'Transfer', manual: true },
  { key: 'mex', apAsignable: true, label: 'MEX', manual: true },
  { key: 'facturacion', label: 'Facturación', manual: true },
  { key: 'inlandMx', apAsignable: true, label: 'Inland MX', manual: true },
  { key: 'transfer2', apAsignable: true, label: 'Transfer (USA)', manual: true },
  { key: 'inlandUsa', apAsignable: true, label: 'Inland USA', manual: true },
  { key: 'locales', apAsignable: true, label: 'Locales', manual: true },
  { key: 'seguro', apAsignable: true, label: 'Seguro', manual: true },
  { key: 'otro', apAsignable: true, label: 'Otro', manual: true },
  { key: 'aa', label: 'AA', manual: true },
  { key: 'cerrado', label: 'Cerrado', manual: true },
  { key: 'venta', label: 'Venta' },
  { key: 'costo', label: 'Costo' },
  { key: 'profit', label: 'Profit' },
  { key: 'comments', label: 'Comments', manual: true, ancho: 'min-w-[14rem]' },
];

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
/** '2026-09-18…' → '18-sep-2026' */
function fmtDia(v: unknown): string {
  const m = String(v ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${Number(m[3])}-${MESES[Number(m[2]) - 1]}-${m[1]}` : String(v ?? '');
}

function fmtCell(key: string, v: unknown): string {
  if (v == null || v === '') return '';
  if (key === 'etd' || key === 'eta') return fmtDia(v);
  if (key === 'venta' || key === 'costo' || key === 'profit') {
    const n = Number(v);
    return isNaN(n) ? String(v) : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
  return String(v);
}

/** Celda manual: click para editar, Enter/blur para guardar, Esc cancela.
 *  `fallback` (ej. fecha AR automática) se muestra cuando no hay texto manual;
 *  al editar solo se toca lo manual — borrar el texto regresa al fallback. */
function EditableCell({
  row,
  campo,
  editable,
  fallback,
}: {
  row: OpsRow;
  campo: string;
  editable: boolean;
  fallback?: string;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [valor, setValor] = useState('');
  const mut = useMutation({
    mutationFn: (v: string) =>
      api(`/reports/operaciones/info/${row.bookingId}`, {
        method: 'PATCH',
        body: JSON.stringify({ [campo]: v }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reporte-operaciones'] });
      qc.invalidateQueries({ queryKey: ['ops-info', row.bookingId] });
    },
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar'),
  });

  const actual = row[campo] == null ? '' : String(row[campo]);
  if (!editing) {
    return (
      <div
        className={cn(
          'min-h-[1.5rem] px-1 rounded truncate whitespace-nowrap',
          editable && 'cursor-text hover:bg-amber-50 hover:outline hover:outline-1 hover:outline-amber-300',
        )}
        title={
          !actual && fallback
            ? `Fecha AR automática${editable ? ' — click para escribir otro valor' : ''}`
            : editable
              ? 'Click para editar'
              : undefined
        }
        onClick={() => {
          if (!editable) return;
          setValor(actual);
          setEditing(true);
        }}
      >
        {actual || fallback || (editable ? <span className="text-ink-200">—</span> : '')}
      </div>
    );
  }
  const guardar = () => {
    setEditing(false);
    if (valor !== actual) mut.mutate(valor);
  };
  return (
    <input
      autoFocus
      className="w-full min-w-[7rem] px-1 py-0.5 text-sm border border-brand-400 rounded bg-surface-1"
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onBlur={guardar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') guardar();
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

const fmtFecha = (iso?: string | null) => (iso ? fmtDia(iso) : '');
const nomProv = (p: ApPago) => p.creditorNombre || p.creditor || '?';
const fmtMonto = (p: ApPago) =>
  p.monto != null
    ? `${p.moneda ?? ''} ${p.monto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim()
    : '';

/** Celda de pago a proveedor (BL…Otro / USA…Otro): muestra el texto manual o
 *  la post date del pago AP asignado. Click → menú para asignar un pago
 *  disponible (los ya asignados a otra columna no aparecen), quitar el
 *  asignado o escribir texto libre. */
function ApCell({ row, campo, editable }: { row: OpsRow; campo: string; editable: boolean }) {
  const qc = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');

  const pagos = row.apPagos ?? [];
  const asignado = pagos.find((p) => p.columna === campo);
  const disponibles = pagos.filter((p) => !p.columna);
  const manual = row[campo] == null ? '' : String(row[campo]);
  const display = manual || (asignado ? fmtFecha(asignado.postDate) : '');

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['reporte-operaciones'] });
    qc.invalidateQueries({ queryKey: ['ops-info', row.bookingId] });
  };
  const asignar = useMutation({
    mutationFn: (v: { pagoId: number; columna: string | null }) =>
      api(`/reports/operaciones/ap-pago/${v.pagoId}`, {
        method: 'PATCH',
        body: JSON.stringify({ columna: v.columna }),
      }),
    onSuccess: invalidar,
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo asignar el pago'),
  });
  const guardarTexto = useMutation({
    mutationFn: (v: string) =>
      api(`/reports/operaciones/info/${row.bookingId}`, {
        method: 'PATCH',
        body: JSON.stringify({ [campo]: v }),
      }),
    onSuccess: invalidar,
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar'),
  });

  if (!editable) {
    return (
      <div
        className="min-h-[1.5rem] px-1 truncate whitespace-nowrap"
        title={asignado && !manual ? `${nomProv(asignado)} (${asignado.creditor ?? '?'}) · ${fmtMonto(asignado)} · ${asignado.transRef}` : display || undefined}
      >
        {display}
      </div>
    );
  }
  return (
    <div className="relative">
      <div
        className="min-h-[1.5rem] px-1 rounded truncate whitespace-nowrap cursor-pointer hover:bg-amber-50 hover:outline hover:outline-1 hover:outline-amber-300"
        title={
          asignado && !manual
            ? `${nomProv(asignado)} (${asignado.creditor ?? '?'}) · ${fmtMonto(asignado)} · ${asignado.transRef}`
            : 'Click para asignar un pago o escribir'
        }
        onClick={() => {
          setTexto(manual);
          setAbierto(true);
        }}
      >
        {display || <span className="text-ink-200">—</span>}
      </div>
      {abierto && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setAbierto(false)} />
          <div className="absolute z-30 mt-1 w-72 rounded-md border border-ink-200 bg-surface-1 shadow-lg text-sm">
            <div className="p-2 border-b border-ink-100">
              <input
                autoFocus
                placeholder="Escribir texto libre…"
                className="w-full px-2 py-1 border border-ink-200 rounded bg-surface-1"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (texto !== manual) guardarTexto.mutate(texto);
                    setAbierto(false);
                  }
                  if (e.key === 'Escape') setAbierto(false);
                }}
              />
              <div className="mt-1 text-[11px] text-ink-400">Enter guarda el texto (vacío lo borra)</div>
            </div>
            {asignado && (
              <div className="px-2 py-1.5 flex items-center justify-between gap-2 border-b border-ink-100 bg-surface-2/60">
                <span className="truncate">
                  <b>{fmtFecha(asignado.postDate)}</b> · {nomProv(asignado)} · {fmtMonto(asignado)}
                </span>
                <button
                  className="text-red-500 hover:text-red-700 shrink-0"
                  title="Quitar asignación (el pago vuelve a estar disponible)"
                  onClick={() => {
                    asignar.mutate({ pagoId: asignado.id, columna: null });
                    setAbierto(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="max-h-56 overflow-auto">
              {disponibles.map((p) => (
                <button
                  key={p.id}
                  className="block w-full text-left px-2 py-1.5 hover:bg-surface-2"
                  title={`${p.creditor ?? '?'} · ${p.transRef}${p.descripcion ? ` — ${p.descripcion}` : ''}`}
                  onClick={() => {
                    asignar.mutate({ pagoId: p.id, columna: campo });
                    setAbierto(false);
                  }}
                >
                  <b>{fmtFecha(p.postDate)}</b> · {nomProv(p)} · {fmtMonto(p)}
                  {p.descripcion && (
                    <div className="text-[11px] text-ink-400 truncate">{p.descripcion}</div>
                  )}
                </button>
              ))}
              {disponibles.length === 0 && (
                <div className="px-2 py-2 text-ink-400">Sin pagos AP disponibles</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface OperativoOption {
  id: number;
  nombre: string;
  bookingCount: number;
}

/** Searchbox con autocompletado para filtrar por operativo; la X limpia el filtro. */
function OperativoSearch({
  operativos,
  value,
  onChange,
}: {
  operativos: OperativoOption[];
  value: number | undefined;
  onChange: (id: number | undefined) => void;
}) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const sel = operativos.find((o) => o.id === value);
  const lista = operativos.filter((o) =>
    o.nombre.toLowerCase().includes(texto.trim().toLowerCase()),
  );
  return (
    <div className="relative w-56">
      <Input
        placeholder="Filtrar por operativo…"
        value={sel ? sel.nombre : texto}
        onChange={(e) => {
          setTexto(e.target.value);
          if (sel) onChange(undefined);
          setAbierto(true);
        }}
        onFocus={() => setAbierto(true)}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
        className="pr-7"
      />
      {(sel || texto) && (
        <button
          type="button"
          aria-label="Quitar filtro de operativo"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setTexto('');
            onChange(undefined);
          }}
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {abierto && !sel && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border border-ink-200 bg-surface-1 shadow-lg">
          {lista.map((op) => (
            <button
              key={op.id}
              type="button"
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-2"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(op.id);
                setTexto('');
                setAbierto(false);
              }}
            >
              {op.nombre}
            </button>
          ))}
          {lista.length === 0 && (
            <div className="px-3 py-2 text-sm text-ink-400">Sin coincidencias</div>
          )}
        </div>
      )}
    </div>
  );
}

/** "Maria Alvarado" → "MA": iniciales de las 2 primeras palabras. */
function iniciales(nombre: string | null | undefined): string {
  if (!nombre) return '—';
  return nombre
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function ReportesOperacionesPage() {
  const { user } = useAuth();
  const [vista, setVista] = useState<'maritimo-aereo' | 'otr'>('maritimo-aereo');
  const [search, setSearch] = useState('');
  const [agenteId, setAgenteId] = useState<number | undefined>(undefined);
  const [filtroEstado, setFiltroEstado] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [exporting, setExporting] = useState(false);
  // personalización por usuario: anchos + colores (solo los ve él)
  const [uiPrefs, setUiPrefs] = useTablaPrefs('reporte-operaciones');
  const [menuColor, setMenuColor] = useState<MenuColor | null>(null);
  const setW = (k: string, w: number) => setUiPrefs((p) => ({ ...p, w: { ...p.w, [k]: w } }));

  // Mismo listado de operativos que usa el filtro del board.
  const operativosQ = useQuery({
    queryKey: ['operativos'],
    queryFn: () => api<{ id: number; nombre: string; bookingCount: number }[]>('/bookings/operativos'),
    staleTime: 60_000,
  });

  const q = useQuery({
    queryKey: ['reporte-operaciones', vista, search, agenteId, filtroEstado, page, perPage],
    queryFn: () =>
      api<Paginated<OpsRow>>('/reports/operaciones', {
        params: {
          vista,
          search: search || undefined,
          agenteId,
          estadoEnvio: filtroEstado,
          page,
          perPage,
        },
      }),
  });

  const puedeEditarFila = (row: OpsRow) =>
    user?.rol === 'admin' ||
    user?.rol === 'director' ||
    user?.id === row.agenteId ||
    user?.rol === 'gerente'; // el servidor valida que el agente sea de su equipo

  // Columna Operativo: solo la ven quienes supervisan varios operativos
  // (gerente/admin/supervisor); los operativos y ventas no la necesitan.
  const verOperativo =
    user?.rol === 'gerente' || user?.rol === 'admin' || user?.rol === 'director' || user?.rol === 'supervisor';
  const colsBase = vista === 'otr' ? COLS_OTR : COLS_MAR_AER;
  const cols = verOperativo ? colsBase : colsBase.filter((c) => c.key !== 'operativo');
  // Columnas fijas (sticky) al hacer scroll horizontal: Posición y Cliente
  // para todos; para el gerente (Thalía) también Operativo. Son redimensionables
  // igual que el resto: los offsets se miden en vivo con ResizeObserver.
  const stickyKeys = ['posicion', 'cliente', ...(user?.rol === 'gerente' || user?.rol === 'director' ? ['operativo'] : [])];
  const stickyRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const [stickyW, setStickyW] = useState<Record<string, number>>({});
  useEffect(() => {
    const medir = () => {
      const w: Record<string, number> = {};
      for (const k of stickyKeys) {
        const el = stickyRefs.current[k];
        if (el) w[k] = el.getBoundingClientRect().width;
      }
      setStickyW((prev) =>
        stickyKeys.every((k) => Math.abs((prev[k] ?? 0) - (w[k] ?? 0)) < 0.5) ? prev : w,
      );
    };
    medir();
    const obs = new ResizeObserver(medir);
    for (const k of stickyKeys) {
      const el = stickyRefs.current[k];
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyKeys.join(','), vista]);
  const stickyLeft: Record<string, number> = {};
  {
    // ancho por defecto (px) mientras no haya medición
    const DEF: Record<string, number> = { posicion: 104, cliente: 72, operativo: 40 };
    let acc = 0;
    for (const k of stickyKeys) {
      stickyLeft[k] = acc;
      acc += stickyW[k] ?? DEF[k] ?? 72;
    }
  }
  const rows = q.data?.items ?? [];
  const totalPages = q.data?.pagination.totalPages ?? 1;

  async function onExport() {
    setExporting(true);
    try {
      // El export respeta los mismos filtros que la vista (incl. operativo).
      await apiDownload(
        '/reports/operaciones/export',
        `Reporte Operaciones ${vista === 'otr' ? 'OTR' : 'Mar-Aer'}.xlsx`,
        { vista, search: search || undefined, agenteId, estadoEnvio: filtroEstado },
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 w-[95%] max-w-none mx-auto">
      <style>{`
        .report-scroll { overflow: scroll; max-height: 72vh; }
        .report-scroll::-webkit-scrollbar { width: 14px; height: 14px; }
        .report-scroll::-webkit-scrollbar-track { background: #f1f5f9; }
        .report-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 7px; border: 3px solid #f1f5f9; }
      `}</style>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold">Reportes · Operaciones</h1>
          <p className="text-sm text-ink-400">
            {q.data ? `${q.data.pagination.total} embarques` : 'Cargando…'} · las celdas ámbar se
            editan con un click
          </p>
        </div>
        <Button onClick={onExport} disabled={exporting || !rows.length} size="sm">
          <Download className="h-4 w-4 mr-1" />
          {exporting ? 'Generando…' : 'Exportar a Excel'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-surface-2">
        <div className="flex rounded-md border border-ink-200 overflow-hidden">
          {(
            [
              ['maritimo-aereo', 'Marítimo / Aéreo'],
              ['otr', 'OTR (Terrestre)'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              onClick={() => {
                setVista(v);
                setPage(1);
              }}
              className={cn(
                'px-3 py-1.5 text-sm font-medium',
                vista === v ? 'bg-brand-500 text-white' : 'bg-surface-1 text-ink-500 hover:bg-surface-2',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <Input
          placeholder="Buscar posición o cliente…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-56"
        />
        {(operativosQ.data?.length ?? 0) > 1 && (
          <OperativoSearch
            operativos={operativosQ.data ?? []}
            value={agenteId}
            onChange={(id) => {
              setAgenteId(id);
              setPage(1);
            }}
          />
        )}
        <Select
          value={filtroEstado ?? '__all__'}
          onValueChange={(v) => {
            setFiltroEstado(v === '__all__' ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los status</SelectItem>
            {(['origin', 'in_transit', 'arrived', 'closed'] as const).map((e) => (
              <SelectItem key={e} value={e}>
                {ESTADO_ENVIO_LABEL[e]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="report-scroll rounded-lg border border-ink-200 bg-surface-1">
        <table className="text-sm border-collapse" style={{ tableLayout: 'auto', width: 'max-content', minWidth: '100%' }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              {cols.map((c) => {
                const esSticky = c.key in stickyLeft;
                return (
                  <th
                    key={c.key}
                    ref={(el) => {
                      if (esSticky) stickyRefs.current[c.key] = el;
                    }}
                    className={cn(
                      'px-2 py-2 text-left font-medium border border-ink-100 whitespace-nowrap',
                      c.manual && 'bg-amber-50/70 text-amber-700',
                      esSticky && 'bg-surface-2',
                    )}
                    style={{
                      overflow: 'hidden',
                      position: 'relative',
                      ...(uiPrefs.w[c.key]
                        ? { width: uiPrefs.w[c.key], minWidth: uiPrefs.w[c.key], maxWidth: uiPrefs.w[c.key] }
                        : { minWidth: '2rem', maxWidth: '40rem' }),
                      ...(esSticky
                        ? { position: 'sticky', left: `${stickyLeft[c.key]}px`, zIndex: 20 }
                        : {}),
                    }}
                    title={c.titulo ?? c.label}
                  >
                    {c.label}
                    <ResizeHandle colKey={c.key} setW={setW} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const editable = puedeEditarFila(r);
              // Toda la fila se pinta del color del estado de envío (pedido
              // de Josué 15-sep); el texto usa el tono de contraste de la paleta.
              const rowColor = ESTADO_ENVIO_COLOR[(r.estadoEnvio as EstadoEnvio) ?? 'origin'];
              return (
                <tr
                  key={r.bookingId}
                  style={rowColor ? { backgroundColor: rowColor.bg, color: rowColor.text } : undefined}
                >
                  {cols.map((c) => {
                    const miColor = colorPersonal(uiPrefs, r.posicion, c.key);
                    const anchoTd = uiPrefs.w[c.key]
                      ? { maxWidth: uiPrefs.w[c.key], overflow: 'hidden' as const }
                      : undefined;
                    return (
                    <td
                      key={c.key}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setMenuColor({ x: e.clientX, y: e.clientY, rowKey: r.posicion, colKey: c.key });
                      }}
                      className={cn(
                        'px-1.5 py-1 border border-ink-100 align-top whitespace-nowrap',
                        c.key === 'estadoEnvio' && 'w-[1%]',
                        // Comments sí se topa (si no, una nota larga descuadra todo)
                        c.key === 'comments' && 'max-w-[24rem] overflow-hidden',
                      )}
                      style={
                        c.key in stickyLeft
                          ? {
                              position: 'sticky',
                              left: `${stickyLeft[c.key]}px`,
                              zIndex: 5,
                              // fondo propio: sin él, lo que se scrollea se
                              // vería a través de la celda fija
                              backgroundColor: miColor ?? rowColor?.bg,
                              ...(miColor ? { color: '#1f2937' } : {}),
                              ...anchoTd,
                            }
                          : {
                              ...(miColor ? { backgroundColor: miColor, color: '#1f2937' } : {}),
                              ...anchoTd,
                            }
                      }
                    >
                      {c.key === 'estadoEnvio' ? (
                        // Mismo selector que el board: cambia el estado desde
                        // el reporte (el API valida permisos; supervisor y
                        // ventas lo ven como etiqueta de solo lectura).
                        <EstadoEnvioSelector
                          bookingId={r.bookingId}
                          value={(r.estadoEnvio as EstadoEnvio) ?? 'origin'}
                          compacto
                        />
                      ) : c.apAsignable ? (
                        <ApCell row={r} campo={c.key} editable={editable} />
                      ) : c.key === 'facturacion' ? (
                        // Manual con fallback: la post date AR automática se ve
                        // cuando el operativo no ha escrito nada.
                        <EditableCell
                          row={r}
                          campo="facturacion"
                          editable={editable}
                          fallback={fmtFecha(r.facturacionAuto)}
                        />
                      ) : c.manual ? (
                        <EditableCell row={r} campo={c.key} editable={editable} />
                      ) : c.autoCapturable && editable && (r[c.key] == null || r[c.key] === '') ? (
                        // Celda AUTO sin dato en el reporte → capturable a mano
                        // (si el reporte lo trae después, el reporte manda).
                        <EditableCell row={r} campo={c.key} editable={editable} />
                      ) : c.key === 'operativo' ? (
                        // Iniciales oficiales de CW (Staff Profile); fallback a
                        // las 2 primeras letras. Nombre completo en el tooltip.
                        <div className="truncate whitespace-nowrap font-medium" title={r.operativo ?? ''}>
                          {(r.operativoIniciales as string) || iniciales(r.operativo)}
                        </div>
                      ) : (
                        <div
                          className={cn(
                            'truncate whitespace-nowrap',
                            c.key === 'posicion' && 'font-medium tabular',
                            // nombres largos: topadas a ~la mitad, el completo
                            // queda en el tooltip
                            ['cliente', 'shipper', 'cnee', 'linea'].includes(c.key) &&
                              'max-w-[10rem]',
                          )}
                          title={fmtCell(c.key, r[c.key])}
                        >
                          {fmtCell(c.key, r[c.key])}
                        </div>
                      )}
                    </td>
                    );
                  })}
                </tr>
              );
            })}
            {rows.length === 0 && !q.isLoading && (
              <tr>
                <td colSpan={cols.length} className="px-3 py-8 text-center text-ink-400">
                  Sin embarques para esos filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Select
          value={String(perPage)}
          onValueChange={(v) => {
            setPerPage(Number(v));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[50, 100, 200, 500].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n} por página
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {menuColor && (
        <ColorMenu
          menu={menuColor}
          onPick={(scope, color) => setUiPrefs((p) => aplicarColor(p, menuColor, scope, color))}
          onClose={() => setMenuColor(null)}
        />
      )}

      {totalPages > 1 && (
          <>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} title="Primera página">
              «
            </Button>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              ‹
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 2)
              .map((n, idx, arr) => (
                <span key={n} className="flex items-center gap-1">
                  {idx > 0 && arr[idx - 1] !== n - 1 && <span className="text-ink-300">…</span>}
                  <Button
                    variant={n === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </Button>
                </span>
              ))}
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              ›
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Última página">
              »
            </Button>
          </>
        )}
        <span className="text-ink-400 ml-1">
          {q.data ? `${q.data.pagination.total} embarques · página ${page} de ${totalPages}` : ''}
        </span>
      </div>
    </div>
  );
}
